import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';

/**
 * Invokes the given method on the backend endpoint passing the given data as parameters
 *
 * @param {any} method the method to invoke on the backend endpoint
 * @param {any} data the data to be passed as part of the invokation on the backend
 * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
 * @returns {Promise} the promise from the backend call
 */
function processSubmission(method, data, additionalHeaders = {}) {
    return JsonRPCService.send(getProxiedServiceUrl('gatewayPolicyChange'), method, data, additionalHeaders);
}

function saveEndorsement(data, additionalHeaders){
    if (Array.isArray(data) && data.length > 0) {
        data[0].beforeSave();
    }
    return processSubmission('save', data, additionalHeaders);
}

/**
 * This is the server responsible for dealing with the Quote and Buy backend operations
 *
 * @export
 * @class EndorsementService
 */
export default class GatewayEndorsementService {
    /**
     * Updates, saves and performs a quote on an existing submission.
     * Generates quotes for all available product offerings.
     *
     * @param {Object} data tge submission that will be saved
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof EndorsementService
     */
    static loadEndorsement(data, additionalHeaders = {}) {
        return processSubmission('load', [data], additionalHeaders);
    }

    static loadEndorsementWithEffectiveDate(data, additionalHeaders = {}) {
        return processSubmission('loadWithEffectiveDate', data, additionalHeaders);
    }

    static saveEndorsement(data, additionalHeaders = {}) {
        return saveEndorsement(data, additionalHeaders);
    }

    static saveAndQuoteSubmission(data, additionalHeaders = {}) {
        return processSubmission('saveAndQuote', data, additionalHeaders);
    }

    static updateCoverages(quoteID, clausesToUpdate, additionalHeaders = {}) {
        return processSubmission('updateCoverages', [quoteID, clausesToUpdate], additionalHeaders);
    }

    static quoteEndorsement(data, additionalHeaders = {}) {
        return processSubmission('quote', data, additionalHeaders);
    }

    static bind(data, additionalHeaders = {}) {
        return processSubmission('bind', data, additionalHeaders);
    }

    static withdrawEndorsement(data, additionalHeaders = {}) {
        return processSubmission('withdraw', data, additionalHeaders);
    }

    static getAvailablePolicy(data, additionalHeaders = {}) {
        return processSubmission('getAvailablePolicy', [data], additionalHeaders);
    }

    static getAvailablePolicies(data, additionalHeaders = {}) {
        return processSubmission('getAvailablePolicies', data, additionalHeaders);
    }

    static checkEffectiveDateIsValid(data, additionalHeaders = {}) {
        return processSubmission('checkEffectiveDateIsValid', data, additionalHeaders);
    }

    static isQuoted(data, additionalHeaders = {}) {
        return processSubmission('isQuoted', data, additionalHeaders);
    }

    static getLatestBoundPolicyChangeEffectiveDate(data, additionalHeaders = {}) {
        return processSubmission('getLatestBoundPolicyChangeEffectiveDate', [data], additionalHeaders);
    }

    static saveAndQuote(data, additionalHeaders = {}) {
        return processSubmission('saveAndQuote', [data], additionalHeaders);
    }

    static syncQuestionSetML(jobID, qsDto, additionalHeaders = {}) {
        return processSubmission('syncQuestionSetML', [jobID, qsDto], additionalHeaders);
    }

    static withdrawJobs (jobNumbers, additionalHeaders = {}) {
        return processSubmission('withdrawJobs', [jobNumbers], additionalHeaders);
    }

    static areJobsBound (jobNumbers, additionalHeaders = {}) {
        return processSubmission('areJobsBound', [jobNumbers], additionalHeaders);
    }

    static isJobBound (jobID, additionalHeaders = {}) {
        return processSubmission('isJobBound', [jobID], additionalHeaders);
    }

    static bindEndorsement (jobNumber, paymentDetails, additionalHeaders = {}) {
        return processSubmission('bind', [jobNumber, paymentDetails], additionalHeaders);
    }

    static createBundleChanges (policyNumbers, policyNumbersToChange, additionalHeaders = {}) {
        return processSubmission('createBundleChanges', [policyNumbers, policyNumbersToChange], additionalHeaders);
    }

    static syncLocationQuestionSet(quoteID, locationID, qsDto, additionalHeaders = {}) {
        return processSubmission('syncLocationQuestionSet', [quoteID, locationID, qsDto], additionalHeaders);
    }

    static updateModifiers(data, additionalHeaders = {}) {
        // Remove portal step to avoid triggering IMF
        data.portalCurrentStep_ML = null;
        return saveEndorsement(data, additionalHeaders);
    }
    
    static getAvailablePaymentPlans(jobNumber, paymentMethod, mortgageePay, mortgageeFirstYear, additionalHeaders = {}) {
        return processSubmission('getPolicyChangePaymentPlans', [jobNumber, paymentMethod, mortgageePay, mortgageeFirstYear], additionalHeaders);
    }

    static evaluateUWPrebind(jobID, additionalHeaders = {}) {
        return processSubmission('evaluateUWPrebind', [jobID], additionalHeaders);
    }

    static HOShouldDisplayAnimalExclusionWarning(quoteID, additionalHeaders = {}) {
        return processSubmission('HOShouldDisplayAnimalExclusionWarning', [quoteID], additionalHeaders);
    }

    static getPolicyChangeDetails (jobNumber, additionalHeaders = {}) {
        return processSubmission('getPolicyChangeDetails', [jobNumber], additionalHeaders);
    }

    static getAdditionalInterestCandidatesML(quoteID, additionalHeaders = {}) {
        return processSubmission('getAdditionalInterestCandidates', [quoteID], additionalHeaders);
    }

    static updateModifiersML(data, additionalHeaders = {}) {
        // Remove portal step to avoid triggering IMF
        data.portalCurrentStep_ML = null;
        return processSubmission('updateModifiers', data, additionalHeaders);
    }

    static callVeriskIntegration (jobId, additionalHeaders = {}) {
        return processSubmission('callVeriskIntegration', [jobId], additionalHeaders);
    }

    static updateDwellingField (jobId, selectedValue, additionalHeaders={}) {
        return processSubmission('updateDwellingField',[jobId, selectedValue], additionalHeaders);
    }
}
